// One source of truth for "in what order does a locker's modules display".
//
// Three surfaces have to agree or the on-screen signage, the printed deposit
// sheet and the physical unit disagree with each other: the OS dashboard, the
// deposit report's A/B/C module letters, and the SA admin's module-order
// editor. Before LOCKER-390 each surface reimplemented the ordering, and the V2
// ones simply ignored `locker.moduleMapping`.
//
// Typed structurally rather than against `Sst2Locker` for the same reason
// `depositReport.ts` is — the callers pass real `Sst2Locker`/
// `Sst2TemplateAssociation` values, which are assignable to these subsets, and
// this module stays free of the heavier sstv2Types import.

export type ModuleMappingEntry = { sstIdx: number; sl2Idx: number }

// The fields this module reads off a V2 `templates_association` entry.
export type TemplateAssociationLike = {
  name?: string | null
  visual_order?: number | null
  template?: unknown
}

// A V2 module: the association entry paired with its ORIGINAL array index.
// The original index is load-bearing — a door references its module through
// `door.locker_group`, which indexes `templates_association` as the API
// returned it, independent of any display sort applied afterwards.
export type V2Module<T extends TemplateAssociationLike> = {
  tmpl: T
  originalIndex: number
}

// The base (pre-`moduleMapping`) V2 module order: one entry per
// `templates_association` element, `template`-less entries dropped, sorted by
// `visual_order`. This is the order the V2 dashboard and `buildV2DepositGrids`
// have always used; `moduleMapping` permutes it rather than replacing it.
export const buildV2ModuleOrder = <T extends TemplateAssociationLike>(
  sst2Locker: { templates_association?: (T | null)[] | null } | null | undefined,
): V2Module<T>[] =>
  (sst2Locker?.templates_association || [])
    .map((tmpl, originalIndex) => ({ tmpl, originalIndex }))
    .filter((e): e is V2Module<T> => !!e.tmpl?.template)
    .sort((a, b) => (a.tmpl.visual_order ?? 0) - (b.tmpl.visual_order ?? 0))

// A `moduleMapping` is only usable if it is a total permutation of the module
// list it is being applied to. Anything else — a mapping saved when the locker
// had a different module count, a duplicated or out-of-range `sl2Idx` — would
// drop or duplicate a module on screen, which is worse than ignoring the
// mapping. LOCKER-390 N3: no migration; a stale mapping falls back to identity
// (i.e. `visual_order`) order rather than being cleared or repaired.
export const isUsableModuleMapping = (
  moduleMapping: ModuleMappingEntry[] | null | undefined,
  moduleCount: number,
): boolean => {
  if (!moduleMapping?.length) return false
  if (moduleMapping.length !== moduleCount) return false

  const seen = new Set<number>()
  for (const m of moduleMapping) {
    const idx = m?.sl2Idx
    if (!Number.isInteger(idx) || idx < 0 || idx >= moduleCount) return false
    if (seen.has(idx)) return false
    seen.add(idx)
  }
  return true
}

// Permute `modules` by the saved mapping, matching V1's semantics exactly
// (`OsLockerDashboard.tsx`): the mapping array's position is the DISPLAY slot
// and its `sl2Idx` is the source index — display slot `i` shows
// `modules[mapping[i].sl2Idx]`. An unusable mapping leaves the order untouched.
export const applyModuleMapping = <T>(
  modules: T[],
  moduleMapping: ModuleMappingEntry[] | null | undefined,
): T[] =>
  isUsableModuleMapping(moduleMapping, modules.length)
    ? (moduleMapping as ModuleMappingEntry[]).map((m) => modules[m.sl2Idx])
    : modules

// The V2 module order actually rendered: base order, then the saved mapping.
export const orderedV2Modules = <T extends TemplateAssociationLike>(
  sst2Locker: { templates_association?: (T | null)[] | null } | null | undefined,
  moduleMapping: ModuleMappingEntry[] | null | undefined,
): V2Module<T>[] => applyModuleMapping(buildV2ModuleOrder(sst2Locker), moduleMapping)
